import * as React from 'react';
import { FunctionComponent, useState } from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { stripVariableChips } from '../HTMLEditorQuill/HTMLEditorQuill';
import {
  buildPreviewContext,
  resolveGeneralMessagePreview
} from 'Src/helpers/generalMessagePreviewResolver/generalMessagePreviewResolver';
import { buildGmEmailHeaderHtml } from 'Src/helpers/gmEmailTemplate/gmEmailTemplate';

interface Props {
  user: AppUser;
  isOpen: boolean;
  onClose: () => void;
  emailSubject: string;
  emailBody: string;
  pushTitle: string;
  pushBody: string;
  smsText: string;
  senderDisplayName: string;
  isEmailEnabled: boolean;
  isPushEnabled: boolean;
  isSmsEnabled: boolean;
}

type PreviewTab = 'email' | 'push' | 'sms';

function buildEmailPreviewHtml(bodyHtml: string, ctx: any, user: any): string {
  const school = propz.get(user, ['activeSchool'], {});
  const headerColor = propz.get(school, ['gmEmailHeaderColor'], '#1e3a5f');
  const headerEnabled = propz.get(school, ['gmEmailHeaderEnabled'], true);
  const logoEnabled = propz.get(school, ['gmEmailHeaderLogoEnabled'], true);
  const nameEnabled = propz.get(school, ['gmEmailHeaderNameEnabled'], true);
  const footerHtml = propz.get(school, ['gmEmailFooterHtml'], '');
  const logoAlignment = propz.get(school, ['gmEmailHeaderLogoAlignment'], 'left');
  const namePlacement = propz.get(school, ['gmEmailHeaderNamePlacement'], 'below');
  const nameAlignment = propz.get(school, ['gmEmailHeaderNameAlignment'], 'left');
  const footerBackgroundColor = propz.get(school, ['gmEmailFooterBackgroundColor'], '#f8f9fa');
  const footerTextColor = propz.get(school, ['gmEmailFooterTextColor'], '#64748b');
  const footerAlignment = propz.get(school, ['gmEmailFooterAlignment'], 'left');
  const schoolName = propz.get(school, ['name'], '');

  let logoUrl = propz.get(school, ['pic'], '') || propz.get(school, ['logoUrl'], '') || propz.get(school, ['logo'], '');
  if (logoUrl && logoUrl.startsWith('//')) {
    logoUrl = `https:${logoUrl}`;
  }

  const headerHtml = buildGmEmailHeaderHtml(
    {
      gmEmailHeaderLogoEnabled: logoEnabled,
      gmEmailHeaderNameEnabled: nameEnabled,
      gmEmailHeaderLogoAlignment: logoAlignment,
      gmEmailHeaderNamePlacement: namePlacement,
      gmEmailHeaderNameAlignment: nameAlignment
    },
    schoolName,
    logoUrl
  );

  return `
    <table width="100%" cellpadding="0" cellspacing="0"
      style="background:#f4f4f4;padding:16px 0;font-family:Arial,sans-serif;">
      <tr><td align="center">
        <table width="100%" cellpadding="0" cellspacing="0" style="max-width:100%;width:100%;">
          ${
            headerEnabled
              ? `<tr>
            <td style="background:${headerColor};padding:16px 20px;border-radius:8px 8px 0 0;">
              ${headerHtml}
            </td>
          </tr>`
              : ''
          }
          <tr>
            <td style="background:#ffffff;padding:20px;font-size:14px;line-height:1.5;color:#1a1a1a;${
              headerEnabled ? '' : 'border-radius:8px 8px 0 0;'
            }">
              <style>
                .preview-body{max-width:100%;overflow-wrap:anywhere}
                .preview-body p{margin:0;padding:0}
                .preview-body p+p{margin-top:0.3em}
                .preview-body img{max-width:100%!important;height:auto!important;object-fit:contain}
              </style>
              <div class="preview-body">${bodyHtml ||
                '<span style="color:#94a3b8;font-style:italic">No email body</span>'}</div>
            </td>
          </tr>
          ${
            footerHtml
              ? `
          <tr>
            <td align="${footerAlignment}" style="background:${footerBackgroundColor};padding:14px 20px;border-radius:0 0 8px 8px;
              border-top:1px solid #e2e8f0;font-size:12px;color:${footerTextColor};line-height:1.5;text-align:${footerAlignment};">
              <style>
                .preview-footer{max-width:100%;overflow-wrap:anywhere}
                .preview-footer p{margin:0;padding:0}
                .preview-footer p+p{margin-top:0.3em}
                .preview-footer img{max-width:100%!important;height:auto!important;object-fit:contain}
              </style>
              <div class="preview-footer">${footerHtml}</div>
            </td>
          </tr>`
              : ''
          }
        </table>
      </td></tr>
    </table>`;
}

export const GeneralMessagePreviewModal: FunctionComponent<Props> = props => {
  const {
    user,
    isOpen,
    onClose,
    emailSubject,
    emailBody,
    pushTitle,
    pushBody,
    smsText,
    senderDisplayName,
    isEmailEnabled,
    isPushEnabled,
    isSmsEnabled
  } = props;

  const [activeTab, setActiveTab] = useState<PreviewTab>('email');

  if (!isOpen) return null;

  const ctx = buildPreviewContext(user, senderDisplayName);

  const resolved = resolveGeneralMessagePreview(
    emailSubject,
    stripVariableChips(emailBody),
    pushTitle,
    pushBody,
    smsText,
    ctx
  );
  const enabledTabs: PreviewTab[] = [
    ...(isEmailEnabled ? ['email' as PreviewTab] : []),
    ...(isPushEnabled ? ['push' as PreviewTab] : []),
    ...(isSmsEnabled ? ['sms' as PreviewTab] : [])
  ];
  const effectiveTab = enabledTabs.indexOf(activeTab) !== -1 ? activeTab : enabledTabs[0];

  const tabStyle = (tab: PreviewTab): React.CSSProperties => ({
    padding: '8px 20px',
    border: 'none',
    borderBottom: effectiveTab === tab ? '3px solid #3b5bdb' : '3px solid transparent',
    background: 'none',
    color: effectiveTab === tab ? '#3b5bdb' : '#64748b',
    fontWeight: effectiveTab === tab ? 700 : 400,
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.15s'
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)'
      }}
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          width: '780px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: '16px' }}>Message preview</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
              Showing with sample recipient: <strong>Sarah Thompson</strong>, child: <strong>Oliver Thompson</strong>,
              Year <strong>{ctx.childYearGroup}</strong>, {ctx.childForm}, {ctx.childHouse}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#64748b',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', padding: '0 24px' }}>
          {isEmailEnabled && (
            <button type="button" style={tabStyle('email')} onClick={() => setActiveTab('email')}>
              📧 Email
            </button>
          )}
          {isPushEnabled && (
            <button type="button" style={tabStyle('push')} onClick={() => setActiveTab('push')}>
              📱 Push
            </button>
          )}
          {isSmsEnabled && (
            <button type="button" style={tabStyle('sms')} onClick={() => setActiveTab('sms')}>
              💬 SMS
            </button>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {effectiveTab === 'email' && (
            <div>
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '16px'
                }}
              >
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>FROM</div>
                <div style={{ fontWeight: 600 }}>{ctx.fromDisplayName}</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', marginBottom: '4px' }}>SUBJECT</div>
                <div style={{ fontWeight: 600 }}>
                  {resolved.emailSubject || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No subject</span>}
                </div>
              </div>
              <div style={{ borderRadius: '8px', overflow: 'hidden' }}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: buildEmailPreviewHtml(resolved.emailBody, ctx, user)
                  }}
                />
              </div>
            </div>
          )}

          {effectiveTab === 'push' && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div
                style={{
                  width: '260px',
                  height: '520px',
                  backgroundImage: 'url(/dist/images/iphone_white.png)',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center top',
                  backgroundSize: '100% auto',
                  position: 'relative'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '130px',
                    left: '12%',
                    right: '12%',
                    background: '#ffffffee',
                    borderRadius: '14px',
                    padding: '10px 12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                    <img
                      src="/dist/images/logoImage.png"
                      alt="SiT"
                      style={{ width: '20px', height: '20px', borderRadius: '4px' }}
                    />
                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Squad In Touch</span>
                    <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: 'auto' }}>now</span>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '3px', color: '#1e293b' }}>
                    {resolved.pushTitle || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No title</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: '#374151', lineHeight: 1.4 }}>
                    {resolved.pushBody || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No body</span>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {effectiveTab === 'sms' && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div
                style={{
                  width: '260px',
                  height: '520px',
                  backgroundImage: 'url(/dist/images/iphone_black.png)',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center top',
                  backgroundSize: '100% auto',
                  position: 'relative'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '90px',
                    left: '12%',
                    right: '12%',
                    textAlign: 'center',
                    fontSize: '11px',
                    color: '#fff',
                    opacity: 0.7
                  }}
                >
                  {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div
                  style={{
                    position: 'absolute',
                    top: '130px',
                    left: '14%',
                    right: '14%',
                    background: '#e9e9eb',
                    borderRadius: '16px 16px 16px 4px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    lineHeight: 1.5,
                    color: '#1e293b'
                  }}
                >
                  {resolved.smsText || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No SMS text</span>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 24px',
            borderTop: '1px solid #e2e8f0',
            fontSize: '11px',
            color: '#94a3b8',
            textAlign: 'center'
          }}
        >
          This is a preview with sample data. Actual messages will use real recipient data.
        </div>
      </div>
    </div>
  );
};

GeneralMessagePreviewModal.displayName = 'GeneralMessagePreviewModal';
